import { useEffect, useMemo } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { buildCatalogMenuStructure } from "../utils/catalogUtils";
import { buildCatalogSelectionBreadcrumb, resolveCatalogSelection } from "../../../shared/utils/familyMenu";
import { resolveCatalogRoute } from "../../../shared/utils/topbarUtils";

const useCatalogFilters = (dynamicFamilies) => {
  const location = useLocation();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const menuStructure = useMemo(() => buildCatalogMenuStructure(dynamicFamilies), [dynamicFamilies]);
  const isLegacyCatalogPath = location.pathname.startsWith("/catalogo");
  const collectionFromQuery = searchParams.get("collection") || "";
  const subcollectionFromQuery = searchParams.get("subcollection") || "";
  const hasFamilyQuery = searchParams.has("familia");
  const familyFromQuery = Number(searchParams.get("familia") || 0);
  const searchQuery = searchParams.get("search") || "";
  const refreshToken = searchParams.get("refresh") || "";
  const resolvedRoute = useMemo(
    () => resolveCatalogRoute({
      menuStructure,
      sectionSlug: params.collectionSlug || params.slug || "",
      subcollectionSlug: params.subcollectionSlug || "",
      familySlug: params.familySlug || "",
      leafSlug: params.leafSlug || "",
    }),
    [menuStructure, params.collectionSlug, params.familySlug, params.leafSlug, params.slug, params.subcollectionSlug]
  );
  const collection = isLegacyCatalogPath ? collectionFromQuery : resolvedRoute.collection;
  const subcollection = isLegacyCatalogPath ? subcollectionFromQuery : resolvedRoute.subcollection;

  const selection = useMemo(
    () => resolveCatalogSelection(menuStructure, collection, subcollection),
    [menuStructure, collection, subcollection]
  );
  const currentFamilyId = hasFamilyQuery
    ? (familyFromQuery > 0 ? familyFromQuery : 0)
    : (isLegacyCatalogPath ? selection.currentFamilyId : resolvedRoute.familyId || selection.currentFamilyId);
  const shouldShowProducts = Boolean(searchQuery) || selection.mode === "products" || selection.mode === "all";
  const breadcrumbItems = useMemo(
    () => buildCatalogSelectionBreadcrumb(selection),
    [selection]
  );

  useEffect(() => {
    if (!isLegacyCatalogPath) {
      return;
    }

    if (!collectionFromQuery && !subcollection && !searchQuery && currentFamilyId <= 0 && !refreshToken) {
      return;
    }

    const currentFamilyParam = hasFamilyQuery ? String(familyFromQuery > 0 ? familyFromQuery : 0) : "";
    const nextFamilyParam = hasFamilyQuery
      ? String(currentFamilyId > 0 ? currentFamilyId : 0)
      : (currentFamilyId > 0 ? String(currentFamilyId) : "");
    if (
      collectionFromQuery === collection
      && subcollectionFromQuery === subcollection
      && currentFamilyParam === nextFamilyParam
    ) {
      return;
    }

    const nextParams = new URLSearchParams();
    if (collection) {
      nextParams.set("collection", collection);
    }
    if (subcollection) {
      nextParams.set("subcollection", subcollection);
    }
    if (hasFamilyQuery) {
      nextParams.set("familia", String(currentFamilyId > 0 ? currentFamilyId : 0));
    } else if (currentFamilyId > 0) {
      nextParams.set("familia", String(currentFamilyId));
    }
    if (searchQuery) {
      nextParams.set("search", searchQuery);
    }
    if (refreshToken) {
      nextParams.set("refresh", refreshToken);
    }
    setSearchParams(nextParams, { replace: true });
  }, [
    collection,
    collectionFromQuery,
    currentFamilyId,
    familyFromQuery,
    hasFamilyQuery,
    isLegacyCatalogPath,
    refreshToken,
    searchQuery,
    setSearchParams,
    subcollection,
    subcollectionFromQuery,
  ]);

  return {
    collection,
    subcollection,
    searchQuery,
    refreshToken,
    currentFamilyId,
    selectionMode: selection.mode,
    navigationItems: selection.navigationItems,
    shouldShowProducts,
    breadcrumbItems,
  };
};

export default useCatalogFilters;
